import { TimeFormatEnum } from 'common/components/entities/Calendar/constants'
import { TypedTFunctionInterface } from 'src/shared/i18next/types/typed-t-function-interface'
import {
  getReadableMonthName,
  getReadableShortWeekdayName,
  timeZones,
} from './constants'

export const isDatesEqual = (x: Date | string, y: Date | string) => {
  return new Date(x).toLocaleDateString() === new Date(y).toLocaleDateString()
}

export const secondsToHours = (
  seconds: number,
  hourCycle: TimeFormatEnum = TimeFormatEnum.h24,
) => {
  return Intl.DateTimeFormat('utc', {
    hour: 'numeric',
    minute: 'numeric',
    // has to be timezone agnostic since we only store time (without date)
    timeZone: 'UTC',
    hourCycle,
  }).format(seconds * 1000)
}

export const getTimeZoneTime = (
  tz: string,
  hourCycle: TimeFormatEnum = TimeFormatEnum.h24,
) => {
  if (!timeZones.includes(tz)) {
    return ''
  }

  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: 'numeric',
    hourCycle,
  }).format(new Date())
}

export const getAllTimeZones = (
  hourCycle: TimeFormatEnum = TimeFormatEnum.h24,
) => {
  const list: { label: string; value: string }[] = []

  timeZones.forEach(tz => {
    const time = getTimeZoneTime(tz, hourCycle)

    list.push({
      label: `${tz} (${time})`,
      value: tz,
    })
  })

  return list
}

export const joinDateWithTimeSlot = (date?: Date, timeSlot?: number) => {
  if (!date || typeof timeSlot !== 'number') return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  const hours = String(Math.floor(timeSlot / 3600)).padStart(2, '0')
  const minutes = String(Math.floor((timeSlot % 3600) / 60)).padStart(2, '0')
  const seconds = String(timeSlot % 60).padStart(2, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
}

export const getStartEndDates = (month: Date) => {
  const startDate = new Date(month.getFullYear(), month.getMonth(), 1)
    .toISOString()
    .slice(0, 10)
  const endDate = new Date(month.getFullYear(), month.getMonth() + 1, 1)
    .toISOString()
    .slice(0, 10)

  return { startDate, endDate }
}

export const formatDate = (date: Date, t: TypedTFunctionInterface) => {
  const weekday = t(getReadableShortWeekdayName(date))
  const month = t(getReadableMonthName(date))

  return `${weekday},\u00A0${month} ${date.getDate()}` // Tue, December 23
}

export const formatDateTime = (
  selectedDate: Date,
  selectedTimeSlot: number,
  t: TypedTFunctionInterface,
  timeFormat: TimeFormatEnum = TimeFormatEnum.h24,
) => {
  const date = new Date(selectedDate)

  const time = secondsToHours(selectedTimeSlot, timeFormat)
  const weekday = t(getReadableShortWeekdayName(date))
  const month = t(getReadableMonthName(date))
  const day = date.getDate()
  const year = date.getFullYear()

  return `${time}, ${weekday}, ${month} ${day}, ${year}` // 15:00, Fri, January 3, 2025
}
